import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { breakpoints } from 'javascripts/shared/utils/breakpoint_utils';
import { useClickOutside } from 'javascripts/hooks/use-click-outside';
import { useMediaQuery } from 'react-responsive';

import { AccessibleRadioButtonGroup } from 'javascripts/components/accessible_radio_button_group';

import { ReactComponent as CheckIcon } from 'images/check-icon-colorless.svg';

import styles from 'stylesheets/components/chirp_select';

export function ChirpSelect({ children, onSelect, options, selectLabel, selectedValue }) {
  const isMobile = useMediaQuery({ maxWidth: breakpoints.xs });

  const [showOptions, setShowOptions] = useState(false);
  const clickTargetRef = useClickOutside(() => {
    setShowOptions(false);
  });

  function radioOption(radioProps) {
    return (
      <div
        alt={radioProps.label}
        aria-checked={radioProps.value === selectedValue}
        className={styles.option}
        key={radioProps.id || radioProps.value}
        onClick={() => radioProps.onSelect(radioProps.value)}
        onKeyDown={radioProps.onKeyDown}
        ref={radioProps.radioRef}
        role='radio'
        tabIndex={radioProps.selected || radioProps.overrideIndex ? '0' : '-1'}
      >
        <div className={styles.checkbox}>
          {radioProps.value === selectedValue ? <CheckIcon className={styles.selected} /> : null}
        </div>
        {radioProps.label}
      </div>
    );
  }

  function renderDesktopOptions() {
    function handleSelect(value) {
      setShowOptions(false);
      onSelect(value);
    }

    return (
      <div
        ref={clickTargetRef}
        className={classNames(styles.optionsDropdown, { [styles.hidden]: !showOptions || isMobile })}
      >
        <AccessibleRadioButtonGroup
          activeId={selectedValue}
          onSelect={handleSelect}
          options={options}
          RadioComponent={radioOption}
        />
      </div>
    );
  }

  function renderMobileOptions() {
    function onChange(event) {
      onSelect(event.target.value);
    }

    return (
      <select className={styles.mobileSelect} onChange={onChange} value={selectedValue} aria-label={selectLabel}>
        {options.map(({ label, value }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className={styles.chirpSelect}>
      <div onClick={() => setShowOptions(true)} role='button' aria-label={selectLabel}>
        {children}
      </div>
      {isMobile && renderMobileOptions()}
      {renderDesktopOptions()}
    </div>
  );
}

ChirpSelect.propTypes = {
  children: PropTypes.node,
  onSelect: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  selectLabel: PropTypes.string,
  selectedValue: PropTypes.string,
};
